import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { messages } from './messages';

const styles = cssMap({
	progressTrack: {
		display: 'block',
		width: '100%',
		height: '4px',
		overflow: 'hidden',
		borderRadius: token('radius.small'),
		backgroundColor: token('elevation.surface.pressed'),
	},
	progressIndicatorDefault: {
		display: 'block',
		height: '100%',
		borderRadius: token('radius.small'),
		backgroundColor: token('color.background.neutral.bold'),
	},
	progressIndicatorWarning: {
		display: 'block',
		height: '100%',
		borderRadius: token('radius.small'),
		backgroundColor: token('color.background.warning.bold'),
	},
	progressIndicatorError: {
		display: 'block',
		height: '100%',
		borderRadius: token('radius.small'),
		backgroundColor: token('color.background.danger.bold'),
	},
});

export type AssessmentResultProgressAppearance = 'default' | 'warning' | 'error';

export type AssessmentResultProgressData = {
	percentage: number;
	appearance: AssessmentResultProgressAppearance;
};

const progressIndicatorStyles = {
	default: styles.progressIndicatorDefault,
	warning: styles.progressIndicatorWarning,
	error: styles.progressIndicatorError,
} as const;

type AssessmentResultProgressProps = {
	dataPoint: string;
	valueText: string;
	progress: AssessmentResultProgressData;
};

export const AssessmentResultProgress = ({
	dataPoint,
	valueText,
	progress,
}: AssessmentResultProgressProps) => {
	const { formatMessage } = useIntl();
	const percentage = Math.max(0, Math.min(100, progress.percentage));

	return (
		<Box
			as="span"
			role="progressbar"
			aria-label={formatMessage(messages.progressBarLabel, { dataPoint })}
			aria-valuemin={0}
			aria-valuemax={100}
			aria-valuenow={percentage}
			aria-valuetext={valueText}
			xcss={styles.progressTrack}
		>
			<Box
				as="span"
				style={{ width: `${percentage}%` }}
				xcss={progressIndicatorStyles[progress.appearance]}
			/>
		</Box>
	);
};
